import { useState, useMemo, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Eye,
  MoreHorizontal,
  UserCheck,
  UserX,
  Mail,
  Calendar,
  Clock,
  AlertTriangle,
  KeyRound,
  Users,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useInventory, SystemUser } from '@/context/InventoryContext';
import { SaveIndicator } from '@/components/ui/save-indicator';
import { useAuthStore } from '@/stores/auth';
import { TotpSetup } from '@/components/auth/TotpSetup';
import { StatusCard } from '@/components/dashboard/StatusCard';

// Helper to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().token;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

type UserRole = 'admin' | 'manager' | 'operator' | 'viewer';

interface ApiResponse {
  success: boolean;
  data?: { id: number } & Record<string, unknown>;
  error?: { code: string; message: string };
}

const roleConfig: Record<UserRole, { label: string; color: string; icon: typeof Shield; permissions: string[] }> = {
  admin: {
    label: 'Administrator',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    icon: ShieldAlert,
    permissions: ['Full system access', 'User management', 'System settings', 'All data operations'],
  },
  manager: {
    label: 'Manager',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    icon: ShieldCheck,
    permissions: ['View all data', 'Create/Edit orders', 'Manage inventory', 'Generate reports'],
  },
  operator: {
    label: 'Operator',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    icon: Shield,
    permissions: ['View assigned tasks', 'Update work orders', 'Record inventory transactions'],
  },
  viewer: {
    label: 'Viewer',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    icon: Eye,
    permissions: ['View-only access', 'View reports', 'View dashboards'],
  },
};

export function AdminPage() {
  const { systemUsers, setSystemUsers, saveStatus } = useInventory();
  const currentUser = useAuthStore((state) => state.user);

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [userToDelete, setUserToDelete] = useState<SystemUser | null>(null);
  const [userToResetPassword, setUserToResetPassword] = useState<SystemUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'viewer' as UserRole,
    is_active: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // Fetch TOTP status on mount
  useEffect(() => {
    const fetchTotpStatus = async () => {
      try {
        const response = await fetch('/api/v1/auth/totp/status', {
          headers: getAuthHeaders(),
          cache: 'no-store',
        });
        const data = await response.json() as { success: boolean; data?: { enabled: boolean } };
        if (data.success && data.data) {
          setTotpEnabled(data.data.enabled);
        }
      } catch (error) {
        console.error('Failed to fetch TOTP status:', error);
      }
    };
    fetchTotpStatus();
  }, []);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return systemUsers.filter(user => {
      const matchesSearch =
        user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [systemUsers, searchQuery, roleFilter, statusFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: systemUsers.length,
    active: systemUsers.filter(u => u.is_active).length,
    admins: systemUsers.filter(u => u.role === 'admin').length,
    managers: systemUsers.filter(u => u.role === 'manager').length,
  }), [systemUsers]);

  // Handlers
  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role: 'viewer',
      is_active: true,
    });
    setApiError('');
    setUserDialogOpen(true);
  };

  const handleEditUser = (user: SystemUser) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      password: '', // Don't show existing password
      role: user.role,
      is_active: user.is_active,
    });
    setApiError('');
    setUserDialogOpen(true);
  };

  const handleDeleteClick = (user: SystemUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        const response = await fetch(`/api/v1/users/${userToDelete.id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        const data: ApiResponse = await response.json();
        if (!data.success) {
          console.error('Failed to delete user:', data.error);
        }
      } catch (error) {
        console.error('Error deleting user:', error);
      }
      // Always update local state
      setSystemUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleToggleStatus = (user: SystemUser) => {
    setSystemUsers(prev =>
      prev.map(u =>
        u.id === user.id ? { ...u, is_active: !u.is_active } : u
      )
    );
  };

  const handleResetPasswordClick = (user: SystemUser) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setApiError('');
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (!userToResetPassword || !newPassword) return;

    setIsResettingPassword(true);
    setApiError('');

    try {
      const response = await fetch(`/api/v1/users/${userToResetPassword.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password: newPassword }),
      });

      const data: ApiResponse = await response.json();
      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to reset password');
      }

      setResetPasswordDialogOpen(false);
      setUserToResetPassword(null);
      setNewPassword('');
    } catch (error: any) {
      setApiError(error.message || 'An error occurred');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.full_name) return;
    if (!editingUser && !formData.password) {
      setApiError('Password is required for new users');
      return;
    }

    setIsSubmitting(true);
    setApiError('');

    try {
      if (editingUser) {
        // Update existing user
        const response = await fetch(`/api/v1/users/${editingUser.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            email: formData.email,
            full_name: formData.full_name,
            role: formData.role,
            is_active: formData.is_active,
            ...(formData.password ? { password: formData.password } : {}),
          }),
        });

        const data: ApiResponse = await response.json();
        if (!data.success) {
          throw new Error(data.error?.message || 'Failed to update user');
        }

        // Update local state
        setSystemUsers(prev =>
          prev.map(u =>
            u.id === editingUser.id
              ? { ...u, ...formData, password: undefined }
              : u
          )
        );
      } else {
        // Create new user
        const response = await fetch('/api/v1/users', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name,
            role: formData.role,
            is_active: formData.is_active,
          }),
        });

        const data: ApiResponse = await response.json();
        if (!data.success || !data.data) {
          throw new Error(data.error?.message || 'Failed to create user');
        }

        // Add to local state
        const newUser: SystemUser = {
          id: data.data.id,
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          is_active: formData.is_active,
          created_at: new Date().toISOString(),
        };
        setSystemUsers(prev => [...prev, newUser]);
      }
      setUserDialogOpen(false);
    } catch (error: any) {
      setApiError(error.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Admin Panel</h2>
            <SaveIndicator status={saveStatus} />
          </div>
          <p className="text-muted-foreground">
            Manage users, roles, and system permissions
          </p>
        </div>
        <Button onClick={handleAddUser}>
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Stats Cards - Modern Floating Design */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard
          title="Total Users"
          value={stats.total.toString()}
          subtext={`${stats.total} registered`}
          trend={stats.total > 0 ? 'up' : 'neutral'}
          type="custom"
          icon={Users}
          iconColor="text-blue-600 dark:text-blue-400"
          iconBgColor="bg-blue-100 dark:bg-blue-900/30"
          gradientColor="blue"
        />

        <StatusCard
          title="Active Users"
          value={stats.active.toString()}
          subtext={stats.total - stats.active > 0 ? `${stats.total - stats.active} inactive` : 'All active'}
          trend={stats.active === stats.total ? 'up' : 'neutral'}
          type="custom"
          icon={UserCheck}
          iconColor="text-green-600 dark:text-green-400"
          iconBgColor="bg-green-100 dark:bg-green-900/30"
          gradientColor="green"
        />

        <StatusCard
          title="Administrators"
          value={stats.admins.toString()}
          subtext={`${stats.admins} admin accounts`}
          trend="neutral"
          type="custom"
          icon={ShieldAlert}
          iconColor="text-red-600 dark:text-red-400"
          iconBgColor="bg-red-100 dark:bg-red-900/30"
          gradientColor="red"
        />

        <StatusCard
          title="Managers"
          value={stats.managers.toString()}
          subtext={`${stats.managers} manager accounts`}
          trend="neutral"
          type="custom"
          icon={ShieldCheck}
          iconColor="text-purple-600 dark:text-purple-400"
          iconBgColor="bg-purple-100 dark:bg-purple-900/30"
          gradientColor="purple"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">User Management</CardTitle>
          <CardDescription>View and manage system users and their permissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="operator">Operator</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const RoleIcon = roleConfig[user.role].icon;
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="font-semibold text-primary">
                                {user.full_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium">{user.full_name}</div>
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn('gap-1', roleConfig[user.role].color)}>
                            <RoleIcon className="h-3 w-3" />
                            {roleConfig[user.role].label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.is_active ? (
                            <Badge variant="success" className="gap-1">
                              <UserCheck className="h-3 w-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <UserX className="h-3 w-3" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDate(user.created_at, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.last_login ? (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {formatDate(user.last_login, { month: 'short', day: 'numeric' })}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Never</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResetPasswordClick(user)}>
                                <KeyRound className="mr-2 h-4 w-4" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                                {user.is_active ? (
                                  <>
                                    <UserX className="mr-2 h-4 w-4" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="mr-2 h-4 w-4" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDeleteClick(user)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Role Permissions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Permissions</CardTitle>
          <CardDescription>Overview of permissions for each user role</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {(Object.keys(roleConfig) as UserRole[]).map((role) => {
              const config = roleConfig[role];
              const RoleIcon = config.icon;
              return (
                <div key={role} className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={cn('gap-1', config.color)}>
                      <RoleIcon className="h-3 w-3" />
                      {config.label}
                    </Badge>
                  </div>
                  <ul className="space-y-2">
                    {config.permissions.map((perm, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        {perm}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Security Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Manage your account security settings
            {currentUser && (
              <span className="ml-1">
                for <strong>{currentUser.email}</strong>
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TotpSetup
            isEnabled={totpEnabled}
            onStatusChange={setTotpEnabled}
          />
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user details and permissions.' : 'Create a new user account.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {apiError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
                <p className="text-sm text-destructive">{apiError}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                placeholder="Enter full name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">
                Password {editingUser ? '(leave blank to keep current)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder={editingUser ? 'Enter new password' : 'Enter password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value as UserRole })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 text-red-500" />
                      Administrator
                    </div>
                  </SelectItem>
                  <SelectItem value="manager">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-blue-500" />
                      Manager
                    </div>
                  </SelectItem>
                  <SelectItem value="operator">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-green-500" />
                      Operator
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-gray-500" />
                      Viewer
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {roleConfig[formData.role].permissions[0]}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="is_active" className="text-sm font-normal">
                Account is active
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.email || !formData.full_name || (!editingUser && !formData.password) || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (editingUser ? 'Save Changes' : 'Create User')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete User
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.full_name}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{userToResetPassword?.full_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {apiError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
                <p className="text-sm text-destructive">{apiError}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password *</Label>
              <Input
                id="new_password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)} disabled={isResettingPassword}>
              Cancel
            </Button>
            <Button onClick={handleResetPasswordConfirm} disabled={!newPassword || isResettingPassword}>
              {isResettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
